<template>

    <!-- The Map -->
    <div id="map" />

</template>

<script lang="ts">

import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import { defineComponent, PropType } from 'vue';
import mapboxgl, { EventData, LngLatBoundsLike, LngLatLike, MapboxEvent, Marker, Popup } from 'mapbox-gl';
import { MapboxOptions, MarkerDict, SimpleGeoJsonFeature, GeoJsonFeature } from '@/mls-filter/types';
import oboe from 'oboe';
import qs from 'qs';

export default defineComponent({

    name: 'listing-map',

    data() {
        return {
            map: {} as mapboxgl.Map,
            markers: {} as MarkerDict,
            hoveredAreaId: undefined as string|number|undefined,
            hoveredTooltip: null as Popup|null,
            pinsShouldUpdate: false,
        }
    },

    props: {
        token: {
            type: String,
            required: true
        },
        options: {
            type: Object as PropType<MapboxOptions>,
            required: true,
        },
        // areas: {
        //     type: Array as PropType<string[]>,
        //     default: [],
        // }
    },

    computed: {

        ...mapState([
            'endpoints',
            'region',
            'selected',
            'mapCenter',
            'mapZoom',
            'sorting',
        ]),

        ...mapGetters([
            'serverData',
        ]),

        showMask(): boolean {
            return this.selected.area.length > 0;
        },

        shortAreas(): string[] {
            return this.selected.area.map((a: string) => a.substring(3))
        },

        filterSpec() {
            // returnn mapbox filter spec for this map
            const region: number = this.region;
            const regionCode = region === 1 ? 'NV' : 'WV';
            const filters: any[] = ['all'];

            // add filter for current region
            filters.push([
                'match',
                ['get', 'MLS_AREA'],
                [regionCode],
                true,
                false
            ]);

            // add subarea filter if needed
            if (this.shortAreas.length > 0) {
                filters.push([
                    'match',
                    ['get', 'SUBAREA'],
                    this.shortAreas,
                    false,
                    true
                ]);
            }

            return filters;
        }
    },


    mounted() {

        const isMobile = window.matchMedia('only screen and (max-width: 760px)').matches;

        // create map
        mapboxgl.accessToken = this.token;
        this.map = new mapboxgl.Map({
            container: 'map',
            center: this.mapCenter,
            zoom: this.mapZoom,
            ...this.options
        });

        // disable map rotation
        this.map.dragRotate.disable();
        this.map.touchZoomRotate.disableRotation();

        // add events
        this.map.on('dragend', (ev) => {
            this.setMapCenter({ center: this.map.getCenter(), fetch: !isMobile });
            this.fetchGeoJSON(true, false);
        });

        this.map.on('zoomend', (ev) => {
            this.setMapZoom(this.map.getZoom());
            this.fetchGeoJSON(true, false);
        });

        this.map.on('load', (ev) => {
            // add the source for the MLS subareas
            this.map.addSource('subareas', {
                'type': 'vector',
                'url': 'mapbox://rossettirealty.2f54ad54',
            });
            if(this.showMask) {
                this.enableMaskLayer(isMobile);
            }
        });

        // fetch map markers upon load
        this.fetchGeoJSON(false, false);
        this.pinsShouldUpdate = Object.keys(this.markers).length == 0 && this.selected.area.length > 0;

    },

    watch: {

        showMask(newVal, oldVal) {
            const isMobile = window.matchMedia('only screen and (max-width: 760px)').matches;
            if (newVal !== oldVal) {
                if (newVal === true ) {
                    this.enableMaskLayer(isMobile);
                } else {
                    this.disableMaskLayer(isMobile);
                }
            }
        },

        '$store.state.selected.area': function(newVal, oldVal){
            this.pinsShouldUpdate = true;
            if(this.showMask) {
                this.map.setFilter('mls-subareas', this.filterSpec);
            }
        },

        '$store.state.selected': {
            handler: function(newVal, oldVal) {
                this.fetchGeoJSON(false, true);
            },
            deep: true,
        },
    },

    updated() {
        // this should fire when areas change.
        console.log('updated!');
    },

    methods: {

        ...mapActions(['applyFilterMulti', 'setMapCenter', 'setMapZoom']),

        fetchGeoJSON(withBounds=false, removeStale=false) {

            console.log('Fetching GeoJSON data...');

            const keysFetched: string[] = [];
            const data = {...this.serverData };

            if (withBounds) {
                const bounds = this.map.getBounds();
                data['bounds_sw'] = 'POINT (' + bounds.getWest() + ' ' + bounds.getSouth() + ')';
                data['bounds_ne'] = 'POINT (' + bounds.getEast() + ' ' + bounds.getNorth() + ')';
            }

            const query = qs.stringify(data, {arrayFormat: 'repeat'});

            oboe({
                url: this.endpoints.geojson + '?' + query,
            }).node('features.*', (node, path, ancestors) => {
                if (!(node.mls in this.markers)) {
                    this.addMarker(node);
                }
                if (removeStale) {
                    keysFetched.push(node.mls);
                }

            }).done((geojson) => {

                if (removeStale) {
                    const keysToRemove = Object.keys(this.markers).filter((k) => !(keysFetched.includes(k)));
                    for (const k of keysToRemove) {
                        this.markers[k].remove();
                        delete this.markers[k];
                    }
                }

                if (this.pinsShouldUpdate) {
                    this.updateMarkers(true);
                    this.pinsShouldUpdate = false;
                }

            }).fail((error) => {
                console.log('Error while fetching GeoJSON!');
                console.log(error);
            })

        },

        disableMaskLayer(isMobile: boolean) {
            // console.log('Disabling Mask Layer!');
            // console.log('Mobile:', isMobile);
            this.map.removeLayer('mls-subareas');
            this.map.off('click', this.mouseClick);
            if (!isMobile) {
                this.map.off('mousemove', this.mouseMove);
                this.map.off('mouseleave', this.mouseLeave);
            }
        },

        enableMaskLayer(isMobile: boolean) {

            // console.log('Enabling Mask Layer!');
            // console.log('Mobile:', isMobile);

            // The feature-state dependent fill-opacity expression will render the hover effect
            // when a feature's hover state is set to true.
            this.map.addLayer({
                'id': 'mls-subareas',
                'type': 'fill',
                'source': 'subareas',
                'source-layer': 'subareas-d2qktx',
                'filter': this.filterSpec,
                'layout': {},
                'paint': {
                    'fill-color': '#627BC1',
                    'fill-outline-color': '#0066b7',
                    'fill-opacity': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        0.625,
                        0.25
                    ]
                }
            });

            if (!isMobile) {
                // When the user moves their mouse over the state-fill layer, we'll update the
                // feature state for the feature under the mouse.
                this.map.on('mousemove', 'mls-subareas', this.mouseMove);
                // When the mouse leaves the state-fill layer, update the feature state of the
                // previously hovered feature.
                this.map.on('mouseleave', 'mls-subareas', this.mouseLeave);
            }

            // event to add an area
            this.map.on('click', 'mls-subareas', this.mouseClick);

        },

        mouseMove(ev: EventData) {

            if (this.selected.area.length && ev.features!.length > 0) {

                const id = ev.features![0].id;
                const subarea = ev.features![0].properties!.SUBAREA_DE;

                // hovered area has changed
                if (this.hoveredAreaId && this.hoveredAreaId !== id) {
                    this.map.setFeatureState(
                        { source: 'subareas', sourceLayer: 'subareas-d2qktx', id: this.hoveredAreaId },
                        { hover: false }
                    );
                    if (this.hoveredTooltip) {
                        this.hoveredTooltip.remove();
                    }
                }

                if (this.hoveredAreaId !== id) {

                    // console.log('HOVER', id, subarea);
                    // set new hovered area
                    this.hoveredAreaId = id;

                    this.hoveredTooltip = new mapboxgl.Popup({ closeOnClick: false, closeButton: false })
                        .setHTML('<small>Add ' + subarea + '</small>')
                        .trackPointer()
                        .addTo(this.map);

                    this.map.setFeatureState(
                        { source: 'subareas', sourceLayer: 'subareas-d2qktx', id: this.hoveredAreaId },
                        { hover: true }
                    );
                }
            }
        },

        mouseLeave(ev: EventData) {
            if (this.hoveredAreaId) {
                if (this.hoveredTooltip) {
                    this.hoveredTooltip.remove();
                }
                this.map.setFeatureState(
                    { source: 'subareas', sourceLayer: 'subareas-d2qktx', id: this.hoveredAreaId },
                    { hover: false }
                );
            }
            this.hoveredAreaId = undefined;
        },

        mouseClick(ev: EventData) {
            // if we're already filtering by area, add another
            if(this.selected.area.length > 0) {
                const feature = ev.features![0];
                let areaCode = 'V';
                areaCode += feature!.properties!.MLS_AREA;
                areaCode += feature!.properties!.SUBAREA;
                this.addArea(areaCode);
            }
        },

        // adds an area to the filter upon click
        addArea(areaCode: string) {

            const selectedAreas = this.selected.area as string[];
            if(!selectedAreas.includes(areaCode)) {
                this.applyFilterMulti({
                    filter: 'area',
                    value: selectedAreas.concat([areaCode]),
                });
            } else {
                console.log('Can\'t add area '+ areaCode);
            }
        },

        // create new marker and add it to the map
        addMarker(f: SimpleGeoJsonFeature): Marker {

            const selectedAreas = this.selected.area as string[];
            // grab the popup template
            const popupTemplate = document.getElementById('card-popup');

            // create the pin element
            const el = document.createElement('div');
            el.classList.add('marker');

            // add active class if needed
            el.dataset.area = f.areaCode;
            if (selectedAreas.includes(el.dataset.area) || selectedAreas.length == 0) {
                el.classList.add('active');
            }

            // create the marker
            const marker = new mapboxgl.Marker(el)
                .setOffset([0, -15])
                .setLngLat(f.latlng);

            // create popup for this marker
            if (popupTemplate) {
                const popup = new mapboxgl.Popup({maxWidth: '220px'});
                const popupEl = popupTemplate.cloneNode(true) as HTMLElement;
                popupEl.classList.remove('d-none');
                popupEl.classList.add('card-popup', 'loading');
                popupEl.id = 'popup-' + f.mls;
                popupEl.dataset.mls = f.mls;
                popup.setDOMContent(popupEl);
                popup.on('open', this.loadPopup);
                marker.setPopup(popup);
            }

            marker.addTo(this.map);

            //  assign marker to dict of all markers keyed by MLS
            this.markers[f.mls] = marker;
            return marker;
        },

        loadPopup(ev: EventData) {
            const popup: Popup = ev.target;
            const popupEl = (popup.getElement().querySelector('.card-popup') as HTMLElement);
            if (popupEl && popupEl.classList.contains('loading')) {
                const mls = popupEl.dataset.mls;
                const cardItem = popupEl.querySelector('.card-item');
                const refChild = cardItem!.querySelector('.card-title');
                const loading = document.createElement('div');
                loading.className = 'text-center';
                loading.innerHTML = '<i class="fas fa-circle-notch fa-spin fa-3x my-3"></i>';
                cardItem!.insertBefore(loading, refChild);

                oboe(this.endpoints.popups + mls + '/')
                .done((f: GeoJsonFeature) => {
                    // console.log(f);
                    popupEl.classList.remove('loading');
                    loading.remove();
                    for (const a of popupEl.querySelectorAll('a')) {
                        a.href = f.properties.url;
                    }
                    (popupEl.querySelector('.card-img img')! as HTMLImageElement).src = f.properties.img;
                    popupEl.querySelector('.price a')!.innerHTML = f.properties.price;
                    popupEl.querySelector('.address-street a b')!.innerHTML = f.properties.address;
                    popupEl.querySelector('.address-area a')!.innerHTML = f.properties.area + '<br>' + f.properties.city;
                    popupEl.querySelector('.disclaimer .brokerage')!.innerHTML = f.properties.brokerage;
                    popupEl.querySelector('.disclaimer .mls')!.innerHTML = f.properties.mls;
                })
            }
        },

        updateMarkers(recenter: boolean) {

            console.log('Updating GeoJSON markers on map...');

            const selectedAreas = this.selected.area as string[];

            let minLat = 0;
            let maxLat = 0;
            let minLng = 0;
            let maxLng = 0;
            let numActive = 0;

            const keys = Object.keys(this.markers);

            for(const mls of keys) {

                const marker = this.markers[mls];
                const el = marker.getElement();
                if (el) {
                    const area = el.dataset.area || '';
                    if (selectedAreas.includes(area) || selectedAreas.length == 0) {
                        el.classList.add('active');
                        const lng = marker.getLngLat().lng;
                        const lat = marker.getLngLat().lat;
                        minLat = minLat ? Math.min(minLat, lat) : lat;
                        minLng = minLng ? Math.min(minLng, lng) : lng;
                        maxLat = maxLat ? Math.max(maxLat, lat) : lat;
                        maxLng = maxLng ? Math.max(maxLng, lng) : lng;
                        numActive++;
                    } else {
                        el.classList.remove('active');
                    }
                }
            }

            if (numActive > 0 && recenter === true) {
                const bbox: LngLatBoundsLike = [minLng, minLat, maxLng, maxLat];
                this.map.fitBounds(bbox, {
                    padding: 75,
                    maxZoom: 16.5,
                    duration: 0,
                });
                this.setMapCenter({
                    center: this.map.getCenter(),
                    fetch: false
                });
                this.setMapZoom(this.map.getZoom());
            }
        }

    },

});

</script>

<style lang="scss">

    @import "@/common/scss/_variables.scss";
    @import "@/common/scss/_mixins.scss";






    .mapboxgl-map {

        font: unset !important;
        .mapboxgl-ctrl-bottom-right {
            font: 12px/20px Helvetica Neue,Arial,Helvetica,sans-serif;
        }
    }

    .marker {
        background-image: url('/static/img/map-marker-alt-solid-grey.svg');
        background-size: contain;
        background-position: center bottom;
        background-repeat: no-repeat;
        width: 20px;
        height: 30px;
        cursor: pointer;

        &.active {
            background-image: url('/static/img/map-marker-alt-solid-blue.svg');
        }
    }

    .mapboxgl-popup {


        @include media-breakpoint-down(sm) {
            width: 100% !important;
            max-width: 100% !important;
            transform: unset !important;
            top: -10px;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1000;
        }

        .mapboxgl-popup-content {

            height: 100% !important;

            .mapboxgl-popup-close-button {
                font-size: 30px;
                line-height: 30px;
            }

            .card-popup {

                margin: 10px;

                &.loading {
                    .card-title {
                        display: none;
                    }
                }

                .card-item {

                    padding: 10px !important;

                    @include media-breakpoint-up(md) {
                        padding: 10px 0 0 0 !important;
                        min-height: 300px;
                        min-width: 180px;
                    }
                    @include media-breakpoint-up(xl) {
                        .card-title {
                            .name, .price { font-size: 21px !important; }
                            .address { font-size: 16px !important; }
                        }
                    }

                    .card-details,
                    .widget-favorite {
                        display: none;
                    }
                }
            }
        }


    }


</style>